import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { getTranslation } from '../services/languageService';
import Markdown from '../components/Markdown';
import { getEnabledCategories } from '../services/categoryService';
import shouldUseMockData from '../services/apiConfig';
import './Services.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Services = () => {
  const { language } = useContext(LanguageContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load categories from API, fall back to JSON data
  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      if (shouldUseMockData()) {
        if (!cancelled) setCategories(getEnabledCategories(language));
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories?lang=${language}`);
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) {
            // Only show enabled categories
            const enabled = data.filter(cat => cat.enabled !== false);
            const mapped = enabled.map(cat => ({
              id: cat.key,
              title: cat.label,
              description: cat.description,
              icon: cat.icon || '📋',
              link: `/services/${cat.slug || cat.key}`
            }));
            setCategories(mapped);
            setLoading(false);
          }
          return;
        }
      } catch (error) {
        console.warn('Failed to load categories from API:', error);
      }
      if (!cancelled) {
        setCategories(getEnabledCategories(language));
      }
      setLoading(false);
    };

    loadCategories();
    return () => { cancelled = true; };
  }, [language]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      className="services"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <section className="section">
        <div className="container">
          <motion.h1 className="page-title text-center" variants={itemVariants}>
            {getTranslation('services.title', language)}
          </motion.h1>

          {loading ? (
            <p className="text-center">{getTranslation('common.loading', language)}</p>
          ) : (
            <div className="services-grid">
              {categories.map((category) => (
                <motion.div
                  key={category.id}
                  className="service-card card"
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                >
                  <Link to={category.link} className="service-link">
                    <div className="service-icon">{category.icon}</div>
                    <h2 className="service-title">{category.title}</h2>
                    <Markdown className="service-description">{category.description}</Markdown>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </motion.div>
  );
};

export default Services;
